// ============================================
// CHÁMOMILE - Firebase Configuration
// For: Online Ordering App (Customer-facing)
// ============================================

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  User,
  UserCredential,
  Auth
} from 'firebase/auth';

// ============================================
// SECURITY: Environment Variables (REQUIRED)
// ============================================
// All Firebase credentials MUST be set via environment variables
// This prevents accidental exposure of credentials in source code
// 
// Required environment variables:
// - REACT_APP_FIREBASE_API_KEY
// - REACT_APP_FIREBASE_AUTH_DOMAIN
// - REACT_APP_FIREBASE_PROJECT_ID
// - REACT_APP_FIREBASE_STORAGE_BUCKET
// - REACT_APP_FIREBASE_MESSAGING_SENDER_ID
// - REACT_APP_FIREBASE_APP_ID

// Type-safe Firebase config (values may be undefined until validated)
const firebaseConfig: {
  apiKey: string | undefined;
  authDomain: string | undefined;
  projectId: string | undefined;
  storageBucket: string | undefined;
  messagingSenderId: string | undefined;
  appId: string | undefined;
} = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Validate required configuration
const validateFirebaseConfig = (): boolean => {
  const required = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missing: string[] = [];
  
  required.forEach(key => {
    const value = firebaseConfig[key as keyof typeof firebaseConfig];
    if (!value || value === '' || value === 'your-api-key') {
      missing.push(`REACT_APP_FIREBASE_${key.toUpperCase()}`);
    }
  });
  
  if (missing.length > 0) {
    console.error('❌ Missing required Firebase environment variables:');
    missing.forEach(envVar => console.error(`   - ${envVar}`));
    console.error('\n📋 Please set these in your .env file or Netlify environment variables.');
    console.error('📚 See docs/FIREBASE_SECURITY.md for setup instructions.');
    return false;
  }
  
  return true;
};

// ============================================
// INITIALIZATION WITH VALIDATION
// ============================================

if (!validateFirebaseConfig()) {
  console.error('❌ Firebase configuration is invalid. Please check your environment variables.');
  throw new Error('Firebase configuration error: Missing required environment variables');
}

// After validation, we know all values are defined
// Type assertion is safe here because validateFirebaseConfig ensures all values exist
const validatedConfig = {
  apiKey: firebaseConfig.apiKey!,
  authDomain: firebaseConfig.authDomain!,
  projectId: firebaseConfig.projectId!,
  storageBucket: firebaseConfig.storageBucket!,
  messagingSenderId: firebaseConfig.messagingSenderId!,
  appId: firebaseConfig.appId!
};

// Initialize Firebase
import { initializeAppCheck, ReCaptchaV3Provider, AppCheck } from 'firebase/app-check';

let app: FirebaseApp;
let db: Firestore;
let storage: FirebaseStorage;
let auth: Auth;
let appCheck: AppCheck | undefined;

try {
  app = initializeApp(validatedConfig);
  db = getFirestore(app);
  storage = getStorage(app);
  auth = getAuth(app);
  
  // Initialize Firebase App Check (Protection against unauthorized clients)
  // Ensure the site key is set in .env / Netlify dashboard before enforcing in Firebase Console
  if (process.env.REACT_APP_RECAPTCHA_SITE_KEY && process.env.REACT_APP_RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY_HERE') {
      appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
          isTokenAutoRefreshEnabled: true
      });
  }
} catch (error) {
  console.error('❌ Firebase initialization failed:', error);
  throw new Error('Failed to initialize Firebase services');
}

// Export services
export { db, storage, auth };

// Google Auth Provider with security settings
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account' // Always show account picker for security
});
// Add additional scopes if needed (only request what's necessary)
// googleProvider.addScope('email');
// googleProvider.addScope('profile');

// ============================================
// TYPE DEFINITIONS
// ============================================

interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

// Check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  return validateFirebaseConfig();
};

// ============================================
// SECURITY: Input Validation
// ============================================

// Email validation (RFC 5322 compliant)
const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Additional length check (max 254 chars per RFC)
  return email.length <= 254 && emailRegex.test(email.trim());
};

// Password validation (security best practices)
const isValidPassword = (password: string): boolean => {
  if (!password || typeof password !== 'string') return false;
  // Minimum 8 characters, at least one letter and one number
  // Firebase minimum is 6, but we enforce stronger
  return password.length >= 8 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
};

// Display name validation
const isValidDisplayName = (name: string): boolean => {
  if (!name || typeof name !== 'string') return false;
  // Allow letters, numbers, spaces, and common punctuation
  // Max 50 characters
  const nameRegex = /^[a-zA-Z0-9\s\-'.,]{1,50}$/;
  return nameRegex.test(name.trim());
};

// Sanitize input (prevent XSS)
const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  // Remove potentially dangerous characters
  return input.trim().replace(/[<>]/g, '');
};

// ============================================
// SECURITY: Error Handling
// ============================================

// Error message mapping (generic messages to prevent information leakage)
const getErrorMessage = (code: string): string => {
  // Security: Use generic error messages to prevent user enumeration
  const errors: Record<string, string> = {
    'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'This sign-in method is not available. Please contact support.',
    'auth/weak-password': 'Password must be at least 8 characters with letters and numbers.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'Invalid email or password.', // Generic to prevent enumeration
    'auth/wrong-password': 'Invalid email or password.', // Generic to prevent enumeration
    'auth/invalid-credential': 'Invalid email or password.', // Generic to prevent enumeration
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',
    'auth/popup-blocked': 'Popup was blocked. Please allow popups for this site.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  };
  // Security: Don't expose internal error codes to users
  return errors[code] || 'An error occurred. Please try again.';
};

// Log security events (for monitoring)
const logSecurityEvent = (event: string, details?: Record<string, any>): void => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Security Event] ${event}`, details || '');
  }
  // In production, send to monitoring service (e.g., Sentry, LogRocket)
};

// ============================================
// AUTH FUNCTIONS (with security validation)
// ============================================

// Sign up with email/password
export const signUpWithEmail = async (
  email: string, 
  password: string, 
  displayName: string
): Promise<AuthResult> => {
  // Security: Input validation
  if (!isValidEmail(email)) {
    logSecurityEvent('Invalid email format attempted', { email: email.substring(0, 3) + '***' });
    return { success: false, error: 'Please enter a valid email address.' };
  }

  if (!isValidPassword(password)) {
    logSecurityEvent('Weak password attempted');
    return { success: false, error: 'Password must be at least 8 characters with letters and numbers.' };
  }

  if (!isValidDisplayName(displayName)) {
    return { success: false, error: 'Please enter a valid name (1-50 characters).' };
  }

  // Security: Sanitize inputs
  const sanitizedEmail = email.trim().toLowerCase();
  const sanitizedDisplayName = sanitizeInput(displayName);

  try {
    const userCredential: UserCredential = await createUserWithEmailAndPassword(
      auth, 
      sanitizedEmail, 
      password
    );
    
    // Update profile with sanitized display name
    await updateProfile(userCredential.user, { 
      displayName: sanitizedDisplayName 
    });
    
    // Security: Send email verification (required for security)
    await sendEmailVerification(userCredential.user);
    
    logSecurityEvent('User signed up successfully', { userId: userCredential.user.uid });
    
    return { success: true, user: userCredential.user };
  } catch (error: any) {
    logSecurityEvent('Sign up failed', { error: error.code });
    return { success: false, error: getErrorMessage(error.code) };
  }
};

// Sign in with email/password
export const signInWithEmail = async (
  email: string, 
  password: string
): Promise<AuthResult> => {
  // Security: Input validation
  if (!isValidEmail(email)) {
    logSecurityEvent('Invalid email format on sign in attempt');
    return { success: false, error: 'Please enter a valid email address.' };
  }

  if (!password || password.length < 6) {
    logSecurityEvent('Invalid password format on sign in attempt');
    return { success: false, error: 'Invalid email or password.' }; // Generic message
  }

  // Security: Sanitize email
  const sanitizedEmail = email.trim().toLowerCase();

  try {
    const userCredential: UserCredential = await signInWithEmailAndPassword(
      auth, 
      sanitizedEmail, 
      password
    );
    
    logSecurityEvent('User signed in successfully', { userId: userCredential.user.uid });
    
    return { success: true, user: userCredential.user };
  } catch (error: any) {
    // Security: Log failed attempts (but don't expose details)
    logSecurityEvent('Sign in failed', { error: error.code });
    return { success: false, error: getErrorMessage(error.code) };
  }
};

// Sign in with Google
export const signInWithGoogle = async (): Promise<AuthResult> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    logSecurityEvent('Google sign in successful', { userId: result.user.uid });
    
    return { success: true, user: result.user };
  } catch (error: any) {
    logSecurityEvent('Google sign in failed', { error: error.code });
    
    // Handle specific Google auth errors
    if (error.code === 'auth/popup-blocked') {
      return { success: false, error: 'Popup was blocked. Please allow popups for this site.' };
    }
    
    return { success: false, error: getErrorMessage(error.code) };
  }
};

// Sign out
export const logOut = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const userId = auth.currentUser?.uid;
    await signOut(auth);
    
    logSecurityEvent('User signed out', { userId });
    
    return { success: true };
  } catch (error: any) {
    logSecurityEvent('Sign out failed', { error: error.message });
    return { success: false, error: 'Failed to sign out. Please try again.' };
  }
};

// Password reset
export const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
  // Security: Input validation
  if (!isValidEmail(email)) {
    return { success: false, error: 'Please enter a valid email address.' };
  }

  // Security: Sanitize email
  const sanitizedEmail = email.trim().toLowerCase();

  try {
    // Security: Always return success to prevent email enumeration
    // Firebase will only send email if account exists
    await sendPasswordResetEmail(auth, sanitizedEmail);
    
    logSecurityEvent('Password reset requested', { email: sanitizedEmail.substring(0, 3) + '***' });
    
    // Security: Always return success message (prevents user enumeration)
    return { success: true };
  } catch (error: any) {
    logSecurityEvent('Password reset failed', { error: error.code });
    // Security: Still return success to prevent email enumeration
    // Only show error for invalid email format
    if (error.code === 'auth/invalid-email') {
      return { success: false, error: 'Please enter a valid email address.' };
    }
    // For other errors, return success to prevent enumeration
    return { success: true };
  }
};

// Auth state observer
export const onAuthChange = (callback: (user: User | null) => void): (() => void) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// Check if user has admin role (from custom claims)
export const isUserAdmin = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;
  
  try {
    // Security: Force token refresh to get latest claims
    const tokenResult = await user.getIdTokenResult(true);
    const isAdmin = tokenResult.claims.admin === true;
    
    if (isAdmin) {
      logSecurityEvent('Admin access checked', { userId: user.uid });
    }
    
    return isAdmin;
  } catch (error) {
    logSecurityEvent('Admin check failed', { error: String(error) });
    return false;
  }
};

// Check if user has staff role (from custom claims)
export const isUserStaff = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;
  
  try {
    // Security: Force token refresh to get latest claims
    const tokenResult = await user.getIdTokenResult(true);
    const isStaff = tokenResult.claims.admin === true || tokenResult.claims.staff === true;
    
    if (isStaff) {
      logSecurityEvent('Staff access checked', { userId: user.uid });
    }
    
    return isStaff;
  } catch (error) {
    logSecurityEvent('Staff check failed', { error: String(error) });
    return false;
  }
};

export default app;
